import { InternshipDomain } from "../types";

const SITE_ORIGIN = "https://internship.hitasoft.com";
const LOGO_URL = `${SITE_ORIGIN}/hitasoft_logo.png`;

const HQ_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress:
    "9/1, First & Second Floor, Karthick Centre, Kamala First Street, Chinna Chokkikulam",
  addressLocality: "Madurai",
  addressRegion: "Tamil Nadu",
  postalCode: "625002",
  addressCountry: "IN",
};

/** Returns the current calendar year — updates automatically each January. */
export function getCurrentYear(): number {
  const currentYear = new Date().getFullYear();
  return currentYear;
}

/** Appends the current year to a title segment before the pipe separator. */
export function withSeoYear(title: string, year: number = getCurrentYear()): string {
  const pipeIndex = title.indexOf("|");
  if (pipeIndex === -1) {
    return `${title} ${year}`;
  }
  const left = title.slice(0, pipeIndex).trim();
  const right = title.slice(pipeIndex + 1).trim();
  return `${left} ${year} | ${right}`;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface PageSeo {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
}

export const GENERIC_FAQS: FaqItem[] = [
  {
    question: "What internship programs are available at Hitasoft Talent Lab?",
    answer:
      "Hitasoft Talent Lab offers internship programs in Full Stack Development, Web Development, AI & Machine Learning, Data Science, Android Development, iOS Development, DevOps, Quality Assurance Testing, Marketing, Sales, Human Resources, and UI/UX Design.",
  },
  {
    question: "Are the internships suitable for beginners?",
    answer:
      "Yes. Our internship programs are designed for students, fresh graduates, and career starters. Training begins with fundamentals and gradually progresses to industry-level projects.",
  },
  {
    question: "What skills can I gain through the internship programs?",
    answer:
      "Participants develop technical skills, problem-solving abilities, project experience, collaboration skills, and industry-relevant knowledge based on their chosen specialization.",
  },
  {
    question: "Is the internship available for college students?",
    answer:
      "Yes. College students from engineering, arts, science, commerce, and other academic backgrounds can apply based on program eligibility.",
  },
];

export function getCombinedFaqs(domain: InternshipDomain): FaqItem[] {
  return [...domain.faqs, ...GENERIC_FAQS];
}

export function getHomePageSeo(year: number = getCurrentYear()): PageSeo {
  return {
    title: `Internship in Madurai ${year} | Full Stack, AI, Android & Data Science`,
    description: `Join Hitasoft Talent Lab ${year} – Tamil Nadu's premier corporate internship program. Online internship for students across Tamil Nadu & offline internship in Madurai. Full Stack, ML/AI, Android, DevOps, UI/UX & more. Certified with placement support.`,
    keywords: [
      "internship in Madurai",
      `internship in Madurai ${year}`,
      "software internship in Madurai",
      "internship for engineering students",
      "internship with certificate",
      "internship in Tamil Nadu",
      "online internship Tamil Nadu",
      "software internship Madurai",
      "engineering internship Tamil Nadu",
      "internship for college students",
      "internship for CSE students Tamil Nadu",
      "corporate internship program Tamil Nadu",
      "IT internship Tamil Nadu",
      "hitasoft internship",
      "software developer training Madurai",
      "MERN stack internship Tamil Nadu",
      "android internship Tamil Nadu",
      "machine learning internship Tamil Nadu",
      "devops internship Tamil Nadu",
      "UI UX internship Tamil Nadu",
      "HR internship Tamil Nadu",
      "digital marketing internship Madurai",
      "web development course Tamil Nadu",
      "best internship Madurai",
    ],
    canonicalUrl: `${SITE_ORIGIN}/`,
  };
}

export function getAboutPageSeo(year: number = getCurrentYear()): PageSeo {
  return {
    title: `About Hitasoft Technology Solutions | Corporate Internship Lab Madurai ${year}`,
    description: `Learn about Hitasoft Technology Solutions ${year} – Tamil Nadu's leading corporate internship lab in Madurai. Discover our mission, values, and how we accelerate developer careers through practical corporate mentorship for students across Tamil Nadu.`,
    keywords: [
      "about hitasoft",
      "hitasoft technology solutions",
      "madurai tech companies",
      "software agency Tamil Nadu",
      "talent lab corporate Madurai",
      "corporate internship lab Tamil Nadu",
      "internship company in Madurai",
      "software company Madurai Tamil Nadu",
    ],
    canonicalUrl: `${SITE_ORIGIN}/#about`,
  };
}

export function getTrackPageSeo(domain: InternshipDomain, year: number = getCurrentYear()): PageSeo {
  return {
    title: withSeoYear(domain.metaTitle, year),
    description: `${domain.metaDescription} Apply for ${year} cohorts at Hitasoft Talent Lab, Madurai.`,
    keywords: [
      ...domain.seoKeywords,
      "internship in Madurai",
      "software internship in Madurai",
      "internship for engineering students",
      "internship with certificate",
      `internship ${year}`,
    ],
    canonicalUrl: `${SITE_ORIGIN}/#${domain.shortSlug}-internship`,
  };
}

export function getPageSeo(
  view: "home" | "about" | "track",
  domain?: InternshipDomain,
  year: number = getCurrentYear()
): PageSeo {
  if (view === "about") return getAboutPageSeo(year);
  if (view === "home") return getHomePageSeo(year);
  return getTrackPageSeo(domain!, year);
}

function setMetaContent(
  selector: string,
  name: string,
  content: string,
  attr: "name" | "property" = "name"
): void {
  const matches = document.querySelectorAll(selector);
  matches.forEach((el, index) => {
    if (index > 0) el.remove();
  });

  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkHref(selector: string, rel: string, href: string): void {
  const matches = document.querySelectorAll(selector);
  matches.forEach((el, index) => {
    if (index > 0) el.remove();
  });

  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Applies document title, meta, OG, and Twitter tags from a single PageSeo object. */
export function applyPageSeo(seo: PageSeo): void {
  document.title = seo.title;

  setMetaContent('meta[name="description"]', "description", seo.description);
  setMetaContent('meta[name="keywords"]', "keywords", seo.keywords.join(", "));

  setLinkHref('link[rel="canonical"]', "canonical", seo.canonicalUrl);

  setMetaContent('meta[property="og:title"]', "og:title", seo.title, "property");
  setMetaContent('meta[property="og:description"]', "og:description", seo.description, "property");
  setMetaContent('meta[property="og:url"]', "og:url", seo.canonicalUrl, "property");

  setMetaContent('meta[name="twitter:title"]', "twitter:title", seo.title);
  setMetaContent('meta[name="twitter:description"]', "twitter:description", seo.description);
}

export function buildOrganizationSchema(year: number = getCurrentYear()): object {
  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "EducationalOrganization"],
    "@id": `${SITE_ORIGIN}/#organization`,
    name: "Hitasoft Technology Solutions",
    alternateName: "Hitasoft Talent Lab",
    url: SITE_ORIGIN,
    logo: {
      "@type": "ImageObject",
      url: LOGO_URL,
    },
    image: LOGO_URL,
    description: `Tamil Nadu's leading corporate software internship lab (${year}). Online internship for students across Tamil Nadu. Offline internship exclusively at Madurai office.`,
    address: HQ_ADDRESS,
    areaServed: [
      { "@type": "State", name: "Tamil Nadu" },
      { "@type": "Country", name: "India" },
    ],
    serviceType: "Online and Offline Software Internship Programs",
    availableLanguage: ["Tamil", "English"],
    sameAs: [
      "https://www.facebook.com/hitasoft",
      "https://twitter.com/hitasoft",
      "https://www.linkedin.com/company/hitasoft",
      "https://www.instagram.com/hitasoft_technology_solutions/",
    ],
  };
}

export function buildWebSiteSchema(): object {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_ORIGIN}/#website`,
    url: SITE_ORIGIN,
    name: "Hitasoft Talent Lab",
    description:
      "Corporate software internship programs for students across Tamil Nadu. Offline training in Madurai; online programs statewide.",
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    inLanguage: "en-IN",
  };
}

export function buildWebPageSchema(seo: PageSeo): object {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": seo.canonicalUrl,
    url: seo.canonicalUrl,
    name: seo.title,
    description: seo.description,
    isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
    about: { "@id": `${SITE_ORIGIN}/#organization` },
    inLanguage: "en-IN",
    dateModified: new Date().toISOString().split("T")[0],
  };
}

export function buildEducationalProgramSchema(year: number = getCurrentYear()): object {
  return {
    "@context": "https://schema.org",
    "@type": "EducationalOccupationalProgram",
    "@id": `${SITE_ORIGIN}/#internship-program`,
    name: `Hitasoft Talent Lab Internship Program ${year}`,
    url: SITE_ORIGIN,
    description: `Corporate software internship program ${year} for students across Tamil Nadu. Tracks include Full Stack, ML/AI, Android, iOS, DevOps, QA, UI/UX, and Digital Marketing.`,
    provider: {
      "@type": "EducationalOrganization",
      name: "Hitasoft Talent Lab",
      url: SITE_ORIGIN,
      logo: LOGO_URL,
      sameAs: [
        "https://www.facebook.com/hitasoft",
        "https://www.linkedin.com/company/hitasoft",
        "https://www.instagram.com/hitasoft_technology_solutions/",
      ],
    },
    occupationalCategory: "Software Engineering Internship",
    programType: "Internship",
    timeToComplete: "P12W",
    educationalProgramMode: ["online", "onsite"],
    offers: {
      "@type": "Offer",
      category: "Software Engineering & Business Internship Programs",
      description: `Open year-round internship enrollment for ${year}. Online for all Tamil Nadu students; offline at Madurai HQ.`,
      eligibleRegion: { "@type": "State", name: "Tamil Nadu" },
    },
  };
}

export function buildLocalBusinessSchema(): object {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Hitasoft Talent Lab – Madurai",
    image: LOGO_URL,
    "@id": `${SITE_ORIGIN}/#local-business`,
    url: `${SITE_ORIGIN}/madurai/`,
    sameAs: [
      "https://www.facebook.com/hitasoft",
      "https://www.linkedin.com/company/hitasoft",
      "https://www.instagram.com/hitasoft_technology_solutions/",
    ],
    telephone: "+917708006989",
    priceRange: "$$",
    description:
      "Software internship training center in Madurai, Tamil Nadu. Offline internship programs available at this location. Online internship open to all Tamil Nadu students.",
    address: HQ_ADDRESS,
    geo: {
      "@type": "GeoCoordinates",
      latitude: 9.9252,
      longitude: 78.1198,
    },
    areaServed: [
      { "@type": "City", name: "Madurai" },
      { "@type": "State", name: "Tamil Nadu" },
    ],
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "09:00",
      closes: "18:00",
    },
  };
}

export function buildCourseSchema(domain: InternshipDomain, year: number = getCurrentYear()): object {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "@id": `${SITE_ORIGIN}/#${domain.shortSlug}-course`,
    name: `${domain.title} Internship ${year} – Tamil Nadu`,
    description: domain.metaDescription,
    url: `${SITE_ORIGIN}/#${domain.shortSlug}-internship`,
    provider: {
      "@type": "Organization",
      name: "Hitasoft Talent Lab",
      url: SITE_ORIGIN,
      logo: LOGO_URL,
      sameAs: [
        "https://www.facebook.com/hitasoft",
        "https://www.linkedin.com/company/hitasoft",
      ],
    },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: ["Online", "OnSite"],
      courseWorkload: domain.duration,
      location: {
        "@type": "Place",
        name: "Hitasoft Talent Lab, Madurai (Offline) / Online for all Tamil Nadu",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Madurai",
          addressRegion: "Tamil Nadu",
          addressCountry: "IN",
        },
      },
    },
  };
}

export function buildInternshipTracksItemList(
  domains: InternshipDomain[],
  year: number = getCurrentYear()
): object {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Hitasoft Internship Tracks ${year}`,
    itemListElement: domains.map((domain, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: domain.title,
      url: `${SITE_ORIGIN}/#${domain.shortSlug}-internship`,
    })),
  };
}

export function buildFaqSchema(faqs: FaqItem[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function buildBreadcrumbSchema(
  view: "home" | "about" | "track",
  domain?: InternshipDomain
): object | null {
  if (view === "home") return null;

  const items: object[] = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: SITE_ORIGIN,
    },
  ];

  if (view === "about") {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: "About Hitasoft",
      item: `${SITE_ORIGIN}/#about`,
    });
  } else if (view === "track" && domain) {
    items.push(
      {
        "@type": "ListItem",
        position: 2,
        name: "Internships",
        item: `${SITE_ORIGIN}/#internships`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: domain.title,
        item: `${SITE_ORIGIN}/#${domain.shortSlug}-internship`,
      }
    );
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

export interface PageJsonLdOptions {
  view: "home" | "about" | "track";
  activeDomain: InternshipDomain;
  allDomains: InternshipDomain[];
  year?: number;
}

export function buildPageJsonLdSchemas({
  view,
  activeDomain,
  allDomains,
  year = getCurrentYear(),
}: PageJsonLdOptions): object[] {
  const seo = getPageSeo(view, activeDomain, year);
  const breadcrumb = buildBreadcrumbSchema(view, activeDomain);

  const shared = [
    buildOrganizationSchema(year),
    buildWebSiteSchema(),
    buildWebPageSchema(seo),
    buildEducationalProgramSchema(year),
    buildLocalBusinessSchema(),
  ];

  if (view === "home") {
    return [
      ...shared,
      buildInternshipTracksItemList(allDomains, year),
      buildFaqSchema(getCombinedFaqs(activeDomain)),
    ];
  }

  if (view === "about") {
    return [...shared, ...(breadcrumb ? [breadcrumb] : [])];
  }

  return [
    ...shared,
    buildCourseSchema(activeDomain, year),
    buildFaqSchema(getCombinedFaqs(activeDomain)),
    ...(breadcrumb ? [breadcrumb] : []),
  ];
}

const LEGACY_SCHEMA_IDS = [
  "hitasoft-bootstrap-jsonld",
  "faq-schema-markup",
  "course-schema-markup",
  "hitasoft-faq-schema",
  "hitasoft-course-schema",
  "hitasoft-org-schema",
  "hitasoft-breadcrumb-schema",
  "hitasoft-review-schema",
  "hitasoft-local-schema",
];

/** Removes bootstrap and legacy duplicate schema script tags. */
export function removeLegacySchemaScripts(): void {
  LEGACY_SCHEMA_IDS.forEach((id) => {
    document.getElementById(id)?.remove();
  });

  document.querySelectorAll(".hitasoft-dynamic-seo-schema").forEach((node) => {
    node.remove();
  });

  document
    .querySelectorAll('script[type="application/ld+json"]:not(#hitasoft-jsonld-schema)')
    .forEach((node) => {
      if (!node.id || LEGACY_SCHEMA_IDS.includes(node.id)) {
        node.remove();
      }
    });
}

/** Injects a single consolidated JSON-LD script — prevents duplicate structured data. */
export function applyJsonLdSchemas(schemas: object[]): void {
  removeLegacySchemaScripts();

  let schemaScript = document.getElementById("hitasoft-jsonld-schema");
  if (!schemaScript) {
    schemaScript = document.createElement("script");
    schemaScript.id = "hitasoft-jsonld-schema";
    schemaScript.type = "application/ld+json";
    document.head.appendChild(schemaScript);
  }

  schemaScript.textContent = JSON.stringify(schemas);
}

/** One-call helper: meta tags + consolidated JSON-LD for the active page. */
export function applyPageSeoAndSchemas(options: PageJsonLdOptions): void {
  const year = options.year ?? getCurrentYear();
  const seo = getPageSeo(options.view, options.activeDomain, year);
  applyPageSeo(seo);
  applyJsonLdSchemas(buildPageJsonLdSchemas({ ...options, year }));
}

/** Validates that required SEO tags exist and no duplicate meta tags are present. */
export function validatePageSeo(): { valid: boolean; issues: string[] } {
  const issues: string[] = [];
  const requiredSelectors = [
    'meta[name="description"]',
    'meta[property="og:title"]',
    'meta[property="og:description"]',
    'meta[name="twitter:title"]',
    'meta[name="twitter:description"]',
    'link[rel="canonical"]',
  ];

  requiredSelectors.forEach((selector) => {
    const count = document.querySelectorAll(selector).length;
    if (count === 0) issues.push(`Missing: ${selector}`);
    if (count > 1) issues.push(`Duplicate: ${selector} (${count} found)`);
  });

  const canonical = document.querySelector('link[rel="canonical"]')?.getAttribute("href") ?? "";
  if (canonical.includes(String(getCurrentYear()))) {
    issues.push("Canonical URL must not contain the year");
  }

  const ldJsonScripts = document.querySelectorAll('script[type="application/ld+json"]');
  if (ldJsonScripts.length > 1) {
    issues.push(`Multiple JSON-LD scripts detected (${ldJsonScripts.length})`);
  }

  return { valid: issues.length === 0, issues };
}
